import { defineComponent as _defineComponent } from 'vue'
import { notification } from "ant-design-vue"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"

import { useDownload } from "@/composables/useDownload"
import { TaskState, useTask } from "@/composables/useTask"
import i18n from "@/i18n"
import type { ExistedProductCategory } from "@/modules/offers/types/catalog"
import { getCategories } from "@/services/productCatalogService"
import syncService from "@/services/syncService"
import { TaskStatus } from "@/types/common.type"

type RulesFileFormat = "csv" | "xlsx"

// State

export default /*#__PURE__*/_defineComponent({
  __name: 'SyncRules',
  setup(__props) {

const SEARCH_DELAY = 200

const categoriesTree = ref([])
const searchValue = ref("")
const searchedCategories = ref([])
const selectedCategories = ref([])
const searching = ref(false)

let searchTimerId: ReturnType<typeof setTimeout> | null = null

const categoriesHierarchy = ref<TaskState>({
  taskId: null,
  progress: 0,
  status: null,
  downloadUrl: null
})
const attributesRules = ref<TaskState>({
  taskId: null,
  progress: 0,
  status: null,
  downloadUrl: null
})
const attributeValuesRules = ref<TaskState>({
  taskId: null,
  progress: 0,
  downloadUrl: null,
  status: null
})
// End State

// Computed
const isAttributesRulesLoading = computed(
  () => ![TaskStatus.FAILURE, TaskStatus.SUCCESS, null].includes(attributesRules.value.status)
)
const isAttributeValuesRulesLoading = computed(
  () => ![TaskStatus.FAILURE, TaskStatus.SUCCESS, null].includes(attributeValuesRules.value.status)
)
// End Computed

// Composables
const { pollUrlTask } = useTask()
const { downloadFile } = useDownload()
// End Composables

// Methods
const downloadGeneratedFile = (url: string | null, taskState: TaskState) => {
  let isValidUrl = false

  if (typeof url === "string" && url.trim()) {
    try {
      isValidUrl = ["http:", "https:"].includes(new URL(url).protocol)
    } catch {
      // A completed task may return a missing or malformed download URL.
    }
  }

  if (!isValidUrl) {
    taskState.status = TaskStatus.FAILURE
    taskState.downloadUrl = null
    notification.error({
      message: i18n.t("synchronization.error") as string,
      description: i18n.t("synchronization.file_not_generated") as string
    })
    return
  }

  downloadFile(url)
}
const searchCategories = (tree: any[], value: string) => {
  const result: any[] = []
  const searchQuery = value.trim().toLocaleLowerCase()

  if (!searchQuery) return result

  tree.forEach((node) => {
    if (node.selectable && node.title?.toLocaleLowerCase().includes(searchQuery)) {
      result.push(node)
    }

    if (node.children?.length) {
      result.push(...searchCategories(node.children, value))
    }
  })

  return result
}
const onSearch = () => {
  if (searchTimerId) {
    clearTimeout(searchTimerId)
  }

  const searchQuery = searchValue.value.trim()

  if (!searchQuery) {
    searchedCategories.value = []
    searching.value = false
    searchTimerId = null
    return
  }

  searching.value = true

  searchTimerId = setTimeout(() => {
    searchedCategories.value = searchCategories(categoriesTree.value, searchQuery)
    searching.value = false
    searchTimerId = null
  }, SEARCH_DELAY)
}
const downloadCategoriesHierarchy = async () => {
  const response = await syncService.downloadCategoriesHierarchy()

  categoriesHierarchy.value.taskId = response?.data?.task_id

  const url = await pollUrlTask(
    categoriesHierarchy.value.taskId,
    categoriesHierarchy,
    syncService.checkTaskResults
  )

  downloadGeneratedFile(url, categoriesHierarchy.value)
}
const buildTreeCategoriesData = (
  categories: Array<ExistedProductCategory>,
  parentPath: string = ""
) => {
  const formattedCategories = categories.map((category) => {
    const { name, uuid, children = [] } = category
    const currentPath = parentPath ? `${parentPath} / ${name}` : name

    return {
      title: name,
      value: uuid,
      path: parentPath,
      children: children.length ? buildTreeCategoriesData(children, currentPath) : [],
      selectable: !children.length
    }
  })

  return formattedCategories
}
const getCategoriesInfo = async () => {
  try {
    const { data } = await getCategories()
    categoriesTree.value = [...buildTreeCategoriesData(data.results)]
  } catch (error) {
    if (error?.response?.status === 401) return

    setTimeout(getCategoriesInfo, 5000)
  }
}
const checkSelectedCategories = () => {
  if (selectedCategories.value.length === 0) {
    notification.warning({
      message: i18n.t("synchronization.select_category_to_download_rules_message") as string,
      description: i18n.t("synchronization.select_category_to_download_rules_description") as string
    })
    return false
  }

  return true
}
const downloadAttributesRules = async (fileFormat: RulesFileFormat) => {
  if (!checkSelectedCategories()) return

  attributesRules.value.status = TaskStatus.PENDING

  try {
    const response = await syncService.getAttributesRules(selectedCategories.value, fileFormat)
    attributesRules.value.taskId = response.data.task_id

    const url = await pollUrlTask(
      attributesRules.value.taskId,
      attributesRules,
      syncService.checkCategorySyncRulesTask
    )

    downloadGeneratedFile(url, attributesRules.value)
  } catch (error) {
    attributesRules.value.status = TaskStatus.FAILURE
    console.error(error)
  }
}
const downloadAttributeValuesRules = async (fileFormat: RulesFileFormat) => {
  if (!checkSelectedCategories()) return

  attributeValuesRules.value.status = TaskStatus.PENDING

  try {
    const response = await syncService.getAttributeValuesRules(selectedCategories.value, fileFormat)
    attributeValuesRules.value.taskId = response.data.task_id

    const url = await pollUrlTask(
      attributeValuesRules.value.taskId,
      attributeValuesRules,
      syncService.checkCategorySyncRulesTask
    )

    downloadGeneratedFile(url, attributeValuesRules.value)
  } catch (error) {
    attributeValuesRules.value.status = TaskStatus.FAILURE
    console.error(error)
  }
}
// End Methods

// Lifecycle
onMounted(() => {
  getCategoriesInfo()
})
onUnmounted(() => {
  if (searchTimerId) {
    clearTimeout(searchTimerId)
  }
})

watch(searchValue, onSearch)
// End Lifecycle

return { __sfc: true,SEARCH_DELAY, categoriesTree, searchValue, searchedCategories, selectedCategories, searching, searchTimerId, categoriesHierarchy, attributesRules, attributeValuesRules, isAttributesRulesLoading, isAttributeValuesRulesLoading, pollUrlTask, downloadFile, downloadGeneratedFile, searchCategories, onSearch, downloadCategoriesHierarchy, buildTreeCategoriesData, getCategoriesInfo, checkSelectedCategories, downloadAttributesRules, downloadAttributeValuesRules, TaskStatus }
}

})